import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { uploadFile } from "../../requests/utilities/requests";
import { MAX_STEP_PHOTOS, stepPhotos, withStepPhotos } from "../tasks_hub/taskHubUtils";
import { getSubtaskNumberLabel } from "./subtaskNumbering";
import { TaskSubtaskNameWithRequirement } from "./TaskSubtaskRequirementBadge";

// One editable checklist row. Checkbox rows are tappable across their whole
// width (not just the box), photo requirements read as a state (amber until
// attached), and completed-by attribution shows for shared tasks. The photo
// button is a camera icon beside the note (or the checkbox title), and any
// attached photos show as thumbnails underneath.

// Text fields match the slate-200 borders of the buttons around them.
const FIELD_SX = {
  backgroundColor: "white",
  borderRadius: "8px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

function TaskNameCardInput(props) {
  const [isFileLoading, setIsFileLoading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const handleTextChange = (event, subtaskIndex) => {
    const updatedSubtasks = [...props.modalData.plan_subtask_assignments];
    // A copy, not the row in place: the list behind the modal holds the same
    // objects, and an unsaved answer must not show there as if saved.
    updatedSubtasks[subtaskIndex] = {
      ...updatedSubtasks[subtaskIndex],
      value_text: event.target.value,
    };
    props.setModalData({
      ...props.modalData,
      plan_subtask_assignments: updatedSubtasks,
    });
  };

  const handleCheckChange = (subtaskIndex) => {
    const updatedSubtasks = [...props.modalData.plan_subtask_assignments];
    updatedSubtasks[subtaskIndex] = {
      ...updatedSubtasks[subtaskIndex],
      value_boolean: !updatedSubtasks[subtaskIndex].value_boolean,
    };
    props.setModalData({
      ...props.modalData,
      plan_subtask_assignments: updatedSubtasks,
    });
  };

  // Pass / Fail: tapping the active verdict again clears it.
  const handleVerdict = (subtaskIndex, value) => {
    const updatedSubtasks = [...props.modalData.plan_subtask_assignments];
    const current = updatedSubtasks[subtaskIndex].value_boolean;
    updatedSubtasks[subtaskIndex] = {
      ...updatedSubtasks[subtaskIndex],
      value_boolean: current === value ? null : value,
    };
    props.setModalData({
      ...props.modalData,
      plan_subtask_assignments: updatedSubtasks,
    });
  };

  const setPhotos = (subtaskIndex, photos) => {
    const updatedSubtasks = [...props.modalData.plan_subtask_assignments];
    updatedSubtasks[subtaskIndex] = withStepPhotos(updatedSubtasks[subtaskIndex], photos);
    props.setModalData({
      ...props.modalData,
      plan_subtask_assignments: updatedSubtasks,
    });
  };

  const handleFileChange = async (event, subtaskIndex) => {
    const current = stepPhotos(props.modalData.plan_subtask_assignments[subtaskIndex]);
    const files = Array.from(event.target.files || []).slice(0, MAX_STEP_PHOTOS - current.length);
    // Let the same file be picked again after a removal.
    event.target.value = "";
    if (!files.length) return;
    setIsFileLoading(true);
    const results = await Promise.allSettled(
      files.map((file) => uploadFile(props.homeCompany, file, "plan_subtask_assignment"))
    );
    setIsFileLoading(false);
    const uploaded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    const failed = results.length - uploaded.length;
    if (failed) {
      results
        .filter((r) => r.status === "rejected")
        .forEach((r) => console.error("Failed to upload file:", r.reason));
      toast.error(failed === 1 ? "A photo didn't upload. Try it again." : `${failed} photos didn't upload. Try them again.`);
    }
    if (uploaded.length) setPhotos(subtaskIndex, [...current, ...uploaded]);
  };

  const handleDeleteFile = (subtaskIndex, uuid) => {
    const current = stepPhotos(props.modalData.plan_subtask_assignments[subtaskIndex]);
    setPhotos(subtaskIndex, current.filter((p) => p.uuid !== uuid));
  };

  if (props.subtask.plan_subtask.type === "heading") {
    return (
      <div className="pt-5 pb-1">
        <p className="text-sm font-bold text-slate-700">
          {props.subtask.plan_subtask.name}
        </p>
        {props.subtask.plan_subtask.description && (
          <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words">
            {props.subtask.plan_subtask.description}
          </p>
        )}
      </div>
    );
  }

  const sub = props.subtask.plan_subtask;
  const checked = !!props.subtask.value_boolean;
  const verdict = props.subtask.value_boolean;
  // A tracked task keeps its rows locked until the person presses Start.
  const disabled = !!props.disabled;
  const photoRequired = sub.is_image_required;
  const photos = stepPhotos(props.subtask);
  const canAddPhoto = photos.length < MAX_STEP_PHOTOS;

  const photoLabel = photos.length
    ? "Add another photo"
    : photoRequired
    ? "Add photo (required)"
    : "Add photo";
  const embeddedPhoto = sub.type === "text" || sub.type === "pass_fail";
  const photoButton = canAddPhoto ? (
    <span className="shrink-0 flex self-start" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        title={photoLabel}
        aria-label={isFileLoading ? "Uploading photos" : photoLabel}
        aria-busy={isFileLoading}
        disabled={disabled || isFileLoading}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className={`w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-60 disabled:cursor-not-allowed ${
          embeddedPhoto ? "border-0 bg-transparent" : "border border-slate-200 bg-white"
        } ${photoRequired && !photos.length ? "text-amber-700 hover:bg-amber-50" : "text-slate-500 hover:bg-slate-50"}`}
      >
        {!isFileLoading ? <CameraAltIcon style={{ fontSize: "18px" }} /> : <CircularProgress size="1rem" sx={{ color: "#6d28d9" }} />}
      </button>
      <input
        ref={fileInputRef}
        id={`file-upload-${props.index}`}
        name={`file-upload-${props.index}`}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFileChange(e, props.index)}
        accept=".jpg, .jpeg, .png"
        disabled={disabled || isFileLoading}
      />
    </span>
  ) : null;
  const answerInputProps = {
    sx: { ...FIELD_SX, minHeight: 44, padding: canAddPhoto ? "0 14px 0 0" : "0 14px", alignItems: "flex-start", "& textarea": { padding: "11px 0", lineHeight: "22px" } },
    startAdornment: photoButton,
  };

  const photoStatus = !photos.length && photoRequired ? "Photo required" : "";
  const photoHelper = [photoStatus, isFileLoading ? "Uploading photos…" : ""].filter(Boolean).join(" · ");

  const completedBy =
    props.isAssignedToEveryone &&
    props.subtask.store_team_member &&
    props.subtask.is_completed ? (
      <p className="text-[11px] text-slate-500 break-words">
        {props.subtask.store_team_member?.full_name}
        {props.subtask.completed_at_formatted
          ? ` · ${props.subtask.completed_at_formatted}`
          : ""}
      </p>
    ) : null;

  return (
    <div
      className={`py-3.5 ${
        props.index !== props.modalData.plan_subtask_assignments.length - 1
          ? "border-b border-slate-200"
          : ""
      }`}
    >
      <div className="flex gap-3">
        <div className="text-sm font-semibold text-slate-600 w-5 shrink-0 leading-5 tabular-nums">
          {getSubtaskNumberLabel(
            props.modalData?.plan_subtask_assignments,
            props.index,
            (s) => s.plan_subtask?.type
          )}
        </div>

        {sub.type === "text" ? (
          <div className="flex-1 min-w-0 space-y-2">
            <div className="space-y-2">
              <div>
                <p className="text-sm leading-5 font-medium text-slate-800">
                  <TaskSubtaskNameWithRequirement
                    name={sub.name}
                    isOptional={sub.is_optional}
                    type={sub.type}
                  />
                </p>
                {sub.description && (
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words">
                    {sub.description}
                  </p>
                )}
              </div>
              {completedBy}
            </div>
            <div className="flex gap-2">
              <TextField
                placeholder="Type your answer"
                fullWidth
                variant="outlined"
                size="small"
                name="name"
                value={props.subtask.value_text}
                onChange={(e) => handleTextChange(e, props.index)}
                disabled={disabled}
                multiline
                minRows={1}
                maxRows={4}
                inputProps={{ "aria-label": `Response for ${sub.name}` }}
                InputProps={answerInputProps}
                helperText={photoHelper || undefined}
                FormHelperTextProps={{ role: "status", sx: { marginLeft: 0, color: photoRequired && !photos.length ? "#b45309" : "#64748b" } }}
              />
            </div>
          </div>
        ) : sub.type === "pass_fail" ? (
          <div className="flex-1 min-w-0 space-y-2">
            <div className="space-y-2">
              <div>
                <p className="text-sm leading-5 font-medium text-slate-800">
                  <TaskSubtaskNameWithRequirement
                    name={sub.name}
                    isOptional={sub.is_optional}
                    type={sub.type}
                  />
                </p>
                {sub.description && (
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words">
                    {sub.description}
                  </p>
                )}
              </div>
              {completedBy}
            </div>
            <div className="flex gap-2">
              {[
                [true, "Pass", "border-emerald-600 bg-emerald-600 text-white", "border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-700"],
                [false, "Fail", "border-rose-600 bg-rose-600 text-white", "border-slate-200 text-slate-600 hover:border-rose-400 hover:text-rose-700"],
              ].map(([value, label, activeCls, idleCls]) => (
                <button
                  key={label}
                  type="button"
                  disabled={disabled}
                  aria-pressed={verdict === value}
                  onClick={() => handleVerdict(props.index, value)}
                  className={`flex-1 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${
                    verdict === value ? activeCls : idleCls
                  } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <TextField
                placeholder={verdict === false ? "What did you find? (note)" : "Note (optional)"}
                fullWidth
                variant="outlined"
                size="small"
                value={props.subtask.value_text || ""}
                onChange={(e) => handleTextChange(e, props.index)}
                disabled={disabled}
                multiline
                minRows={1}
                maxRows={4}
                inputProps={{ "aria-label": `Response for ${sub.name}` }}
                InputProps={answerInputProps}
                helperText={photoHelper || undefined}
                FormHelperTextProps={{ role: "status", sx: { marginLeft: 0, color: photoRequired && !photos.length ? "#b45309" : "#64748b" } }}
              />
            </div>
          </div>
        ) : sub.type === "checkbox" ? (
          <div
            className={`flex-1 flex gap-3 ${
              disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer group"
            }`}
            onClick={disabled ? undefined : () => handleCheckChange(props.index)}
          >

            <div
              className={`w-6 h-6 rounded-md flex items-center justify-center border shrink-0 transition-colors ${
                checked
                  ? "bg-violet-700 border-violet-700"
                  : "border-slate-300 bg-white group-hover:border-violet-400"
              }`}
            >
              {checked && (
                <CheckIcon className="text-white" style={{ fontSize: "16px" }} />
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div>
                <p
                  className={`text-sm font-medium transition-colors ${
                    checked ? "text-slate-400 line-through" : "text-slate-800"
                  }`}
                >
                  <TaskSubtaskNameWithRequirement
                    name={sub.name}
                    isOptional={sub.is_optional}
                    type={sub.type}
                  />
                </p>
                {sub.description && (
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words">
                    {sub.description}
                  </p>
                )}
              </div>
              {completedBy}
            </div>
            {photoButton && <div className="self-start h-9 flex">{photoButton}</div>}
          </div>
        ) : null}
      </div>

      {/* Attached photos, under the row */}
      {photos.length > 0 && (
        <div className="mt-2.5 ml-8">
          <div className="flex flex-wrap items-start gap-2">
            {photos.map((photo) => (
              <div key={photo.uuid} className="relative">
                <button
                  type="button"
                  disabled={!photo.presigned_url}
                  aria-label={`View photo for ${sub.name}`}
                  onClick={() => {
                    props.setShowLightbox(true);
                    props.setSelectedImage(photo.presigned_url);
                  }}
                  className="cursor-pointer hover:opacity-90 transition-opacity"
                >
                  {photo.presigned_url ? (
                    <img
                      src={photo.presigned_url}
                      alt="Attached"
                      className="rounded-lg h-[120px] w-[120px] object-cover shadow-sm border border-slate-200"
                    />
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-violet-50 rounded-lg px-3 py-1.5">
                      <CameraAltIcon style={{ fontSize: "15px" }} />
                      Photo attached
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteFile(props.index, photo.uuid)}
                  disabled={disabled}
                  aria-label="Remove photo"
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full border border-slate-200 bg-white hover:border-rose-300 hover:text-rose-600 text-slate-400 flex items-center justify-center transition-colors"
                >
                  <CloseIcon style={{ fontSize: "13px" }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskNameCardInput;
